'use client';

import {useEffect, useId} from 'react';
import {
    MediaPlayer,
    useMediaPlayer,
    useOnPlay,
    type MediaPlayerAssetVideo,
    type MediaPlayerCaption,
    type MediaPlayerCustomizations,
} from '@udemy-v2/react-media-player';
import type {AssetVideo as GraphQLAssetVideo, AssetVideoMashup as GraphQLAssetVideoMashup} from '@/generated/graphql';

export interface MediaPlayerWrapperProps {
    asset?: GraphQLAssetVideo | GraphQLAssetVideoMashup;
    /** Called when playback starts, with the current playback position in seconds */
    onPlay?: (currentTime: number) => void;
    config?: MediaPlayerCustomizations;
}

/**
 * Media player that accepts a GraphQL AssetVideo and handles all react-media-player
 * wiring: store registration, event subscription, and mapping asset captions/thumbnails
 * to MediaPlayer props.
 *
 * Uses the global MediaPlayerProvider from CoreProviders (same as VideoPlayer) so that
 * Shaka is initialized once and reused — avoiding WHEN_TIMEOUT errors that occur when
 * a per-mount provider creates a fresh ShakaPlayerLoader on each React Strict Mode
 * cleanup cycle.
 *
 * Does not contain any domain logic — accepts any GraphQL AssetVideo and can be
 * reused across contexts.
 */
export function MediaPlayerWrapper({asset, onPlay, config}: MediaPlayerWrapperProps) {
    const fallbackId = useId();
    const playerId = asset ? `clp-preview-player-${asset.id}` : fallbackId;
    const mediaPlayer = useMediaPlayer(playerId, config);
    useEffect(() => {
        // Guard against React Strict Mode's doubleInvokeEffectsOnFiber re-running this effect on
        // the same store instance. loadMedia sets store.isLoadingMedia synchronously at the very
        // start (before any awaits), so if a load is already in progress we skip — preventing a
        // concurrent load attempt from the Strict Mode second invocation.
        //
        // Using isLoadingMedia rather than asset.id: after the user switches videos and returns,
        // detach() resets store.src but leaves asset.id set, so an asset.id guard would incorrectly
        // skip the reload on remount. isLoadingMedia is false after detach, so reloads correctly.
        if (asset && !mediaPlayer.isLoadingMedia) {
            mediaPlayer.loadAsset(asset as unknown as MediaPlayerAssetVideo);
        }
    }, [asset, mediaPlayer]);

    useOnPlay(
        playerId,
        () => {
            if (onPlay) {
                onPlay(mediaPlayer.currentTime);
            }
        },
        [onPlay, mediaPlayer],
    );

    const captions: MediaPlayerCaption[] | undefined = asset?.captions
        ?.filter((c) => c.status === 'SUCCESS')
        .map((c) => ({
            id: Number(c.id),
            language: c.language,
            source: c.source,
            status: c.status,
            title: c.videoLabel,
            url: c.url,
            videoLabel: c.videoLabel,
        }));

    return (
        <MediaPlayer
            id={playerId}
            captions={captions}
            thumbSpriteUrl={asset?.thumbnailTrack?.imgUrl}
            thumbnailsVTTUrl={asset?.thumbnailTrack?.vttUrl}
        />
    );
}
